import { useState, useEffect, useMemo } from 'react';
import { fetchBusinessLeads, updateBusinessLead } from '@/lib/api';
import type { BusinessLead, BusinessLeadStatus } from '@/types';
import {
  Search, ChevronDown, ChevronUp, Download, Users,
  Mail, Phone, Globe, AtSign, MessageSquare, StickyNote,
} from 'lucide-react';
import { cn } from '@/utils/helpers';

const STATUS_OPTIONS: { value: BusinessLeadStatus | 'all'; label: string }[] = [
  { value: 'all', label: 'All Statuses' },
  { value: 'new', label: 'New' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'converted', label: 'Converted' },
  { value: 'closed', label: 'Closed' },
];

const STATUS_STYLES: Record<BusinessLeadStatus, string> = {
  new: 'bg-blue-100 text-blue-700',
  contacted: 'bg-amber-100 text-amber-700',
  converted: 'bg-emerald-100 text-emerald-700',
  closed: 'bg-gray-100 text-gray-500',
};

const BUSINESS_TYPE_LABELS: Record<string, string> = {
  travel_agent: 'Travel Agent',
  influencer: 'Influencer',
  tour_operator: 'Tour Operator',
  travel_blogger: 'Travel Blogger',
  other: 'Other',
};

export function BusinessLeadsPage() {
  const [leads, setLeads] = useState<BusinessLead[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<BusinessLeadStatus | 'all'>('all');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [editingNotes, setEditingNotes] = useState<{ id: string; notes: string } | null>(null);

  useEffect(() => {
    loadLeads();
  }, []);

  const loadLeads = async () => {
    try {
      setLoading(true);
      const data = await fetchBusinessLeads();
      setLeads(data);
    } catch (err) {
      console.error('[BusinessLeads] Failed to load:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (leadId: string, status: BusinessLeadStatus) => {
    try {
      const updated = await updateBusinessLead(leadId, { status });
      setLeads(prev => prev.map(l => l.id === leadId ? updated : l));
    } catch (err) {
      console.error('[BusinessLeads] Failed to update status:', err);
    }
  };

  const handleSaveNotes = async () => {
    if (!editingNotes) return;
    try {
      const updated = await updateBusinessLead(editingNotes.id, {
        admin_notes: editingNotes.notes.trim() || null,
      });
      setLeads(prev => prev.map(l => l.id === editingNotes.id ? updated : l));
      setEditingNotes(null);
    } catch (err) {
      console.error('[BusinessLeads] Failed to save notes:', err);
    }
  };

  const filtered = useMemo(() => {
    return leads.filter(l => {
      if (statusFilter !== 'all' && l.status !== statusFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        return (
          l.full_name.toLowerCase().includes(q) ||
          l.email.toLowerCase().includes(q) ||
          (l.business_name?.toLowerCase().includes(q) ?? false)
        );
      }
      return true;
    });
  }, [leads, search, statusFilter]);

  const exportCSV = () => {
    const headers = ['Name', 'Email', 'Phone', 'Business Name', 'Business Type', 'Website', 'Social', 'Expected Users', 'Message', 'Status', 'Admin Notes', 'Date'];
    const rows = filtered.map(l => [
      l.full_name, l.email, l.phone || '', l.business_name || '',
      BUSINESS_TYPE_LABELS[l.business_type] || l.business_type,
      l.website_url || '', l.social_media_handle || '',
      l.expected_users?.toString() || '', l.message || '',
      l.status, l.admin_notes || '',
      new Date(l.created_at).toLocaleDateString(),
    ]);
    const csv = [headers, ...rows].map(r => r.map(c => `"${c.replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `business-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-surface-900">Business Leads</h1>
          <p className="text-sm text-surface-500 mt-1">
            {leads.length} lead{leads.length !== 1 ? 's' : ''} total
          </p>
        </div>
        <button
          onClick={exportCSV}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-xl bg-surface-100 hover:bg-surface-200 text-surface-700 transition-colors"
        >
          <Download size={16} />
          Export CSV
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-surface-400" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by name, email, or business..."
            className="w-full pl-9 pr-4 py-2 text-sm bg-surface-0 border border-surface-300 rounded-xl text-surface-900 placeholder-surface-400 focus:outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/10"
          />
        </div>
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value as BusinessLeadStatus | 'all')}
          className="px-4 py-2 text-sm bg-surface-0 border border-surface-300 rounded-xl text-surface-700 focus:outline-none focus:border-brand-500"
        >
          {STATUS_OPTIONS.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      {/* Table */}
      {filtered.length === 0 ? (
        <div className="glass-panel rounded-2xl p-12 text-center">
          <Users size={40} className="mx-auto mb-3 text-surface-300" />
          <h3 className="text-lg font-semibold text-surface-700 mb-1">No leads found</h3>
          <p className="text-sm text-surface-500">
            {search || statusFilter !== 'all' ? 'Try adjusting your filters.' : 'Business leads will appear here when someone submits the registration form.'}
          </p>
        </div>
      ) : (
        <div className="glass-panel rounded-2xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-surface-200">
                <th className="text-left px-4 py-3 font-medium text-surface-500">Name</th>
                <th className="text-left px-4 py-3 font-medium text-surface-500 hidden sm:table-cell">Email</th>
                <th className="text-left px-4 py-3 font-medium text-surface-500 hidden md:table-cell">Business Type</th>
                <th className="text-left px-4 py-3 font-medium text-surface-500">Status</th>
                <th className="text-left px-4 py-3 font-medium text-surface-500 hidden lg:table-cell">Date</th>
                <th className="w-10" />
              </tr>
            </thead>
            <tbody>
              {filtered.map(lead => {
                const isExpanded = expandedId === lead.id;
                return (
                  <LeadRow
                    key={lead.id}
                    lead={lead}
                    isExpanded={isExpanded}
                    onToggle={() => setExpandedId(isExpanded ? null : lead.id)}
                    onStatusChange={handleStatusChange}
                    editingNotes={editingNotes}
                    onEditNotes={() => setEditingNotes({ id: lead.id, notes: lead.admin_notes || '' })}
                    onNotesChange={val => setEditingNotes(prev => prev ? { ...prev, notes: val } : null)}
                    onSaveNotes={handleSaveNotes}
                    onCancelNotes={() => setEditingNotes(null)}
                  />
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


// ── Lead Row ─────────────────────────────────────────────────────────────────

interface LeadRowProps {
  lead: BusinessLead;
  isExpanded: boolean;
  onToggle: () => void;
  onStatusChange: (leadId: string, status: BusinessLeadStatus) => void;
  editingNotes: { id: string; notes: string } | null;
  onEditNotes: () => void;
  onNotesChange: (val: string) => void;
  onSaveNotes: () => void;
  onCancelNotes: () => void;
}

function LeadRow({
  lead, isExpanded, onToggle, onStatusChange,
  editingNotes, onEditNotes, onNotesChange, onSaveNotes, onCancelNotes,
}: LeadRowProps) {
  const isEditingThis = editingNotes?.id === lead.id;

  return (
    <>
      <tr
        className="border-b border-surface-100 hover:bg-surface-50/50 cursor-pointer transition-colors"
        onClick={onToggle}
      >
        <td className="px-4 py-3">
          <div className="font-medium text-surface-900">{lead.full_name}</div>
          <div className="text-xs text-surface-500 sm:hidden">{lead.email}</div>
        </td>
        <td className="px-4 py-3 text-surface-600 hidden sm:table-cell">{lead.email}</td>
        <td className="px-4 py-3 text-surface-600 hidden md:table-cell">
          {BUSINESS_TYPE_LABELS[lead.business_type] || lead.business_type}
        </td>
        <td className="px-4 py-3">
          <select
            value={lead.status}
            onChange={e => {
              e.stopPropagation();
              onStatusChange(lead.id, e.target.value as BusinessLeadStatus);
            }}
            onClick={e => e.stopPropagation()}
            className={cn(
              'text-xs font-medium px-2.5 py-1 rounded-full border-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-brand-500/20',
              STATUS_STYLES[lead.status],
            )}
          >
            <option value="new">New</option>
            <option value="contacted">Contacted</option>
            <option value="converted">Converted</option>
            <option value="closed">Closed</option>
          </select>
        </td>
        <td className="px-4 py-3 text-surface-500 hidden lg:table-cell">
          {new Date(lead.created_at).toLocaleDateString()}
        </td>
        <td className="px-4 py-3 text-surface-400">
          {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </td>
      </tr>

      {isExpanded && (
        <tr>
          <td colSpan={6} className="px-4 py-4 bg-surface-50/50">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
              {lead.phone && (
                <DetailItem icon={Phone} label="Phone" value={lead.phone} />
              )}
              {lead.business_name && (
                <DetailItem icon={Users} label="Business" value={lead.business_name} />
              )}
              {lead.website_url && (
                <DetailItem icon={Globe} label="Website">
                  <a href={lead.website_url} target="_blank" rel="noopener noreferrer" className="text-brand-500 hover:underline truncate">
                    {lead.website_url.replace(/^https?:\/\//, '')}
                  </a>
                </DetailItem>
              )}
              {lead.social_media_handle && (
                <DetailItem icon={AtSign} label="Social" value={lead.social_media_handle} />
              )}
              {lead.expected_users && (
                <DetailItem icon={Users} label="Expected Users" value={lead.expected_users.toLocaleString()} />
              )}
              {lead.message && (
                <div className="sm:col-span-2 lg:col-span-3">
                  <DetailItem icon={MessageSquare} label="Message" value={lead.message} />
                </div>
              )}
            </div>

            {/* Admin Notes */}
            <div className="mt-4 pt-4 border-t border-surface-200">
              <div className="flex items-center gap-2 mb-2">
                <StickyNote size={14} className="text-surface-400" />
                <span className="text-xs font-medium text-surface-500 uppercase tracking-wide">Admin Notes</span>
              </div>
              {isEditingThis ? (
                <div className="space-y-2">
                  <textarea
                    value={editingNotes!.notes}
                    onChange={e => onNotesChange(e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 text-sm bg-surface-0 border border-surface-300 rounded-lg focus:outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/10 resize-none"
                    placeholder="Add internal notes about this lead..."
                    onClick={e => e.stopPropagation()}
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={e => { e.stopPropagation(); onSaveNotes(); }}
                      className="px-3 py-1.5 text-xs font-medium rounded-lg bg-brand-500 text-white hover:bg-brand-600 transition-colors"
                    >
                      Save
                    </button>
                    <button
                      onClick={e => { e.stopPropagation(); onCancelNotes(); }}
                      className="px-3 py-1.5 text-xs font-medium rounded-lg bg-surface-100 text-surface-600 hover:bg-surface-200 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div
                  onClick={e => { e.stopPropagation(); onEditNotes(); }}
                  className="text-sm text-surface-600 bg-surface-0 rounded-lg px-3 py-2 cursor-text hover:bg-surface-100 transition-colors min-h-[2.5rem]"
                >
                  {lead.admin_notes || <span className="text-surface-400 italic">Click to add notes...</span>}
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}


// ── Detail Item ──────────────────────────────────────────────────────────────

function DetailItem({
  icon: Icon, label, value, children,
}: {
  icon: React.ComponentType<any>;
  label: string;
  value?: string;
  children?: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-2">
      <Icon size={14} className="text-surface-400 mt-0.5 shrink-0" />
      <div className="min-w-0">
        <div className="text-xs text-surface-400">{label}</div>
        {children || <div className="text-surface-700 break-words">{value}</div>}
      </div>
    </div>
  );
}
